import * as React from 'react';
import { Form } from 'antd';
import { NodeBaseModel } from '../models';
import { InstanceUtils, FormUtils } from '../misc';
import { NodeFormValues } from '../types';
import { useAppStore } from '../stores';
import { useNodeSubscriptions, useNodeFormValues } from '.';
import { InputBaseModel } from '../models';
import type { ContractCause } from '../history/ContractDraftHistory';

export default (node: NodeBaseModel) => {
    const { formStore } = useAppStore();

    const [formKey, setFormKey] = React.useState(0);

    const [form] = Form.useForm<NodeFormValues>();

    const isInitialLoad = React.useRef(true);

    const setFormFieldValue = React.useCallback(
        (input: InputBaseModel) => FormUtils.applyInputValue(form, input),
        [form]
    );

    useNodeFormValues(node, setFormFieldValue);
    useNodeSubscriptions(form, node, setFormFieldValue);

    const handleInputValuesChange = (inputGuid: string, value: unknown, cause?: ContractCause) => {
        const input = node.getInputByGuid(inputGuid);

        if (!input) {
            return;
        }

        input.setValue(value);

        input.childInputs
            .filter(InstanceUtils.isInputWithSource)
            .forEach(childInput => childInput.fetchSourceOptionsHierarchy(cause));
    };

    const onValuesChange = (changedValues: NodeFormValues) => {
        const guids = Object.keys(changedValues);
        const inputs = guids.map(guid => node.getInputByGuid(guid)).filter((input): input is InputBaseModel => !!input);
        const values = inputs.map(input => form.getFieldValue(input.guid));
        node.history.editInputs(inputs, (input, index, cause) =>
            handleInputValuesChange(input.guid, values[index], cause)
        );

        if (node.hasError && node.isValid) {
            node.setHasError(false);
        }
    };

    React.useEffect(() => {
        formStore.addFormRef(node.id, form);
        return () => formStore.removeFormRef(node.id);
    }, [node, form, formStore]);

    React.useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            return;
        }

        setFormKey(prevKey => prevKey + 1);
    }, [node]);

    return {
        form,
        formKey,
        onValuesChange
    };
};
